import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Download,
  CloudDownload,
  TableRestaurant,
  KingBed,
  Search,
  Refresh,
  Palette,
  QrCode2,
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import config from '../../config';
import QrAccessSettings from './QrAccessSettings';

/* ──────────────────────────────────────────────────────────────────────────
   QR CODE GENERATOR
   Generates scannable QR codes for every Table and Room. This page ONLY reads
   the existing Table/Room data APIs (same endpoints CreateOrder / RoomList use)
   — it has no ordering/billing business logic. QR images are rendered by the
   public api.qrserver.com service (the same service already used by the UPI QR
   panel in CreateOrder.js), so no extra dependency is added and the server
   carries no QR-rendering load.
   ────────────────────────────────────────────────────────────────────────── */

// Company + auth come from sessionStorage, mirroring CreateOrder.js.
const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const token = sessionStorage.getItem('token');
const getCompanyId = () =>
  company?.id ?? company?.companyId ?? company?.CompanyId ?? null;
// `client` is the company code entered in the login "Company Code" box. It is
// saved to localStorage('client') at login and routes the guest to the right
// company on the /order page (e.g. ?client=Hotelgauravi). We fall back to the
// companyCode/code fields on companyDetails, then the numeric id, just in case.
const getClient = () =>
  localStorage.getItem('client') ||
  company?.companyCode ||
  company?.companycode ||
  company?.code ||
  company?.client ||
  getCompanyId() ||
  '';
const companyName = company?.name || company?.companyName || 'Restaurant';

/* ── THEMES ────────────────────────────────────────────────────────────────
   1 simple theme (the plain black-on-white card from the reference image) plus
   4 rich restaurant themes. Each theme keeps the QR itself high-contrast
   (dark on light) so it always stays scannable, while the surrounding card is
   what becomes "rich" (gradients, gold/white framing, taglines).            */
const THEMES = [
  {
    key: 'simple',
    name: 'Simple',
    bg: ['#FFFFFF', '#F3F4F6'],
    qrDark: '#000000',
    qrLight: '#FFFFFF',
    headerText: '#6B7280',
    labelText: '#111827',
    border: '#E5E7EB',
    tagline: 'Scan the QR code',
    rich: false,
  },
  {
    key: 'royal',
    name: 'Royal Purple',
    bg: ['#4C1D95', '#7C3AED'],
    qrDark: '#3B0764',
    qrLight: '#FFFFFF',
    headerText: '#FBBF24',
    labelText: '#FFFFFF',
    border: '#FBBF24',
    tagline: 'SCAN • ORDER • ENJOY',
    rich: true,
  },
  {
    key: 'emerald',
    name: 'Emerald Dine',
    bg: ['#064E3B', '#10B981'],
    qrDark: '#065F46',
    qrLight: '#FFFFFF',
    headerText: '#ECFDF5',
    labelText: '#FFFFFF',
    border: '#A7F3D0',
    tagline: 'SCAN TO ORDER',
    rich: true,
  },
  {
    key: 'sunset',
    name: 'Sunset Bistro',
    bg: ['#7C2D12', '#F97316'],
    qrDark: '#7C2D12',
    qrLight: '#FFFBEB',
    headerText: '#FFE4D6',
    labelText: '#FFFFFF',
    border: '#FED7AA',
    tagline: 'SCAN • DINE • RELISH',
    rich: true,
  },
  {
    key: 'midnight',
    name: 'Midnight Luxe',
    bg: ['#0F172A', '#1E293B'],
    qrDark: '#0F172A',
    qrLight: '#FFFFFF',
    headerText: '#FBBF24',
    labelText: '#F8FAFC',
    border: '#FBBF24',
    tagline: 'SCAN TO ORDER',
    rich: true,
  },
  {
    // Royal Dining — luxury restaurant palette: purple→charcoal gradient with
    // gold borders and gold lettering for a premium feel. Purple QR on white.
    key: 'royaldining',
    name: 'Royal Dining',
    bg: ['#6B21A8', '#111827'], // primary → secondary
    qrDark: '#6B21A8',
    qrLight: '#FFFFFF',
    headerText: '#FBBF24', // gold accent
    labelText: '#FBBF24',
    border: '#FBBF24', // gold borders
    tagline: 'Scan • Dine • Indulge',
    rich: true,
  },
  {
    // Blue "food doodle" theme — a cream card floating on a blue background,
    // coral "SCAN QR CODE" header, coral QR, and the table/room number shown
    // inside the card. Uses the `framed` layout (everything inside the cream
    // card) instead of the default header-above-panel layout.
    key: 'aqua',
    name: 'Pizza Bistro',
    // Warm "pizza" tones — the outer frame & theme swatch use bg[0] (coral),
    // not aqua/blue. The card fill itself is the cream `panel`.
    bg: ['#F4623A', '#FF8A4C'],
    panel: '#FFF7ED', // cream card
    qrDark: '#F4623A', // coral QR
    qrLight: '#FFF7ED',
    headerText: '#F4623A',
    labelText: '#F4623A',
    border: '#F9C9B0',
    tagline: 'SCAN QR CODE',
    rich: true,
    framed: true,
  },
  {
    // Royal Rajasthan — palace-arch header, golden double border, Rajasthani
    // corner motifs and a royal serif font. Maroon QR on sand-cream.
    key: 'rajasthan',
    name: 'Royal Rajasthan',
    bg: ['#B8860B', '#800020'], // royal gold → maroon (swatch)
    panel: '#FFF8ED', // background
    qrDark: '#800020', // maroon QR
    qrLight: '#FFF8ED',
    headerText: '#800020', // maroon
    labelText: '#800020',
    border: '#B8860B', // royal gold
    accent: '#F8E7C9', // sand beige
    tagline: 'Scan QR Code',
    rich: true,
    rajasthan: true,
  },
  {
    // Vintage Cafe — old coffee-house / bakery look: paper background with a
    // faint paper texture, a coffee-stain ring watermark, tan rounded border
    // and handwritten (cursive) lettering. Coffee-brown QR on paper.
    key: 'cafe',
    name: 'Vintage Cafe',
    bg: ['#6F4E37', '#D4A373'], // coffee → tan (swatch)
    panel: '#FFF8F0', // paper background
    qrDark: '#6F4E37', // coffee brown QR
    qrLight: '#FFF8F0',
    headerText: '#6F4E37',
    labelText: '#6F4E37',
    border: '#D4A373', // tan
    accent: '#FAEDCD',
    tagline: 'Scan & Sip',
    rich: true,
    cafe: true,
  },
  {
    // Wine Cellar Premium — luxury wine-restaurant / vineyard look: cream card
    // with a burgundy border, gold accents, a wine-glass watermark and elegant
    // serif typography. Burgundy QR on cream.
    key: 'wine',
    name: 'Wine Cellar Premium',
    bg: ['#722F37', '#D4AF37'], // burgundy → gold (swatch)
    panel: '#FAF5F0', // background
    qrDark: '#722F37', // wine burgundy QR
    qrLight: '#FAF5F0',
    headerText: '#722F37', // burgundy
    labelText: '#722F37',
    border: '#722F37', // burgundy border
    accent: '#D4AF37', // gold accents
    tagline: 'Scan to Explore',
    rich: true,
    wine: true,
  },
];

const hex = (c) => (c || '').replace('#', '');
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Build the api.qrserver.com image URL for a given payload + theme.
const buildQrSrc = (data, theme, size = 300) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}` +
  `&format=png&margin=10&qzone=1&color=${hex(theme.qrDark)}` +
  `&bgcolor=${hex(theme.qrLight)}&data=${encodeURIComponent(data)}`;

// Base URL the QR codes point to — the origin where THIS web app is actually
// served (window.location.origin), so QR codes always target the current
// deployment's domain instead of a hardcoded host. Set REACT_APP_GUEST_BASE_URL
// to override (e.g. when the guest site lives on a different domain).
const QR_BASE_URL =
  (process.env.REACT_APP_GUEST_BASE_URL || '').trim() ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// The URL a guest lands on after scanning — mirrors QrPageLoader's params.
const buildOrderUrl = (params) => {
  const qs = new URLSearchParams({ client: getClient(), ...params }).toString();
  // Obfuscate params into an opaque token so guests cannot read the client id
  // or table name in the scanned URL. QrPageLoader decodes the ?d=<token> form.
  const token = btoa(encodeURIComponent(qs));
  return `${QR_BASE_URL.replace(/\/$/, '')}/order?d=${encodeURIComponent(token)}`;
};

/* ── Canvas helpers (for downloads) ───────────────────────────────────────── */
// Fetch the QR PNG as a blob and decode it. Using fetch→objectURL (instead of a
// cross-origin <img>) guarantees the canvas is never "tainted", so toBlob()
// works reliably for the download.
const loadQrImage = async (url) => {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`QR fetch failed (HTTP ${resp.status})`);
  const blob = await resp.blob();
  const objUrl = URL.createObjectURL(blob);
  try {
    return await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res({ img: im, objUrl });
      im.onerror = () => rej(new Error('QR image decode failed'));
      im.src = objUrl;
    });
  } catch (e) {
    URL.revokeObjectURL(objUrl);
    throw e;
  }
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// Render one themed QR card to a PNG Blob. This is what both "Download" and
// "Download All" use, so a single download and a bulk download look identical.
const renderCardToBlob = async (entry, theme) => {
  const W = 620;
  const H = 820;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background gradient.
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, theme.bg[0]);
  grad.addColorStop(1, theme.bg[1]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // ── Royal Rajasthan: palace arch, golden double border, corner motifs,
  //    royal serif font. ─────────────────────────────────────────────────
  if (theme.rajasthan) {
    const gold = theme.border; // #B8860B
    const maroon = theme.headerText; // #800020
    const cream = theme.panel; // #FFF8ED
    const serif = 'Georgia, "Times New Roman", serif';

    ctx.fillStyle = cream;
    ctx.fillRect(0, 0, W, H);

    // Golden double border.
    ctx.strokeStyle = gold;
    ctx.lineWidth = 8;
    roundRect(ctx, 18, 18, W - 36, H - 36, 26);
    ctx.stroke();
    ctx.strokeStyle = maroon;
    ctx.lineWidth = 2;
    roundRect(ctx, 36, 36, W - 72, H - 72, 18);
    ctx.stroke();

    ctx.textAlign = 'center';

    // Palace arch (pointed Mughal/Rajasthani arch) — maroon fill, gold outline.
    const acx = W / 2;
    const aHalf = 128;
    const aTop = 72;
    const aBottom = 156;
    ctx.beginPath();
    ctx.moveTo(acx - aHalf, aBottom);
    ctx.lineTo(acx - aHalf, aTop + 34);
    ctx.quadraticCurveTo(acx - aHalf, aTop, acx, aTop - 22);
    ctx.quadraticCurveTo(acx + aHalf, aTop, acx + aHalf, aTop + 34);
    ctx.lineTo(acx + aHalf, aBottom);
    ctx.closePath();
    ctx.fillStyle = maroon;
    ctx.fill();
    ctx.strokeStyle = gold;
    ctx.lineWidth = 4;
    ctx.stroke();
    // Gold finial diamond at the apex.
    ctx.fillStyle = gold;
    ctx.beginPath();
    ctx.moveTo(acx, aTop - 44);
    ctx.lineTo(acx + 9, aTop - 30);
    ctx.lineTo(acx, aTop - 16);
    ctx.lineTo(acx - 9, aTop - 30);
    ctx.closePath();
    ctx.fill();
    // Header inside the arch.
    ctx.fillStyle = gold;
    ctx.font = `700 26px ${serif}`;
    ctx.fillText(theme.tagline.toUpperCase(), acx, aBottom - 36);

    // Rajasthani corner motifs — a gold rosette in each corner.
    const cornerMotif = (x, y) => {
      ctx.fillStyle = gold;
      ctx.beginPath();
      ctx.arc(x, y, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = maroon;
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = gold;
      for (let k = 0; k < 4; k++) {
        const ang = (k * Math.PI) / 2 + Math.PI / 4;
        ctx.beginPath();
        ctx.arc(
          x + Math.cos(ang) * 17,
          y + Math.sin(ang) * 17,
          4,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
    };
    cornerMotif(70, 70);
    cornerMotif(W - 70, 70);
    cornerMotif(70, H - 70);
    cornerMotif(W - 70, H - 70);

    // QR with a gold rounded border.
    const qr = 320;
    const qx = (W - qr) / 2;
    const qy = 218;
    ctx.strokeStyle = gold;
    ctx.lineWidth = 6;
    roundRect(ctx, qx - 20, qy - 20, qr + 40, qr + 40, 16);
    ctx.stroke();
    const raj = await loadQrImage(buildQrSrc(entry.data, theme, 460));
    ctx.drawImage(raj.img, qx, qy, qr, qr);
    URL.revokeObjectURL(raj.objUrl);

    // Table / room number in royal serif.
    ctx.fillStyle = maroon;
    ctx.font = `700 58px ${serif}`;
    ctx.fillText(entry.label, W / 2, qy + qr + 96);
    if (entry.subLabel) {
      ctx.fillStyle = gold;
      ctx.font = `italic 600 26px ${serif}`;
      ctx.fillText(entry.subLabel, W / 2, qy + qr + 134);
    }

    return await new Promise((res) =>
      canvas.toBlob((b) => res(b), 'image/png'),
    );
  }

  // ── Wine Cellar Premium: burgundy border, gold accents, wine-glass
  //    watermark, elegant serif typography. ──────────────────────────────
  if (theme.wine) {
    const wine = theme.headerText; // #722F37
    const gold = theme.accent; // #D4AF37
    const cream = theme.panel; // #FAF5F0
    const serif = 'Georgia, "Times New Roman", serif';

    ctx.fillStyle = cream;
    ctx.fillRect(0, 0, W, H);

    // Faint wine-glass watermarks.
    const wineGlass = (cx, cy, s) => {
      ctx.strokeStyle = 'rgba(114,47,55,0.09)';
      ctx.lineWidth = 4;
      const r = 36 * s;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI, false); // bowl
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx, cy + r);
      ctx.lineTo(cx, cy + r + 50 * s); // stem
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - 26 * s, cy + r + 50 * s);
      ctx.lineTo(cx + 26 * s, cy + r + 50 * s); // base
      ctx.stroke();
    };
    wineGlass(122, 230, 1.3);
    wineGlass(W - 122, H - 300, 1.3);

    // Burgundy border with a gold inner accent line.
    ctx.strokeStyle = wine;
    ctx.lineWidth = 8;
    roundRect(ctx, 18, 18, W - 36, H - 36, 24);
    ctx.stroke();
    ctx.strokeStyle = gold;
    ctx.lineWidth = 2;
    roundRect(ctx, 34, 34, W - 68, H - 68, 18);
    ctx.stroke();

    ctx.textAlign = 'center';

    // Elegant serif header with a gold underline + diamond accents.
    ctx.fillStyle = wine;
    ctx.font = `700 34px ${serif}`;
    ctx.fillText(theme.tagline.toUpperCase(), W / 2, 110);
    ctx.strokeStyle = gold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 110, 128);
    ctx.lineTo(W / 2 + 110, 128);
    ctx.stroke();
    ctx.fillStyle = gold;
    [-118, 118].forEach((dx) => {
      const x = W / 2 + dx;
      const y = 128;
      ctx.beginPath();
      ctx.moveTo(x, y - 6);
      ctx.lineTo(x + 6, y);
      ctx.lineTo(x, y + 6);
      ctx.lineTo(x - 6, y);
      ctx.closePath();
      ctx.fill();
    });

    // QR with a gold outer + burgundy inner border.
    const qr = 330;
    const qx = (W - qr) / 2;
    const qy = 184;
    ctx.strokeStyle = gold;
    ctx.lineWidth = 8;
    roundRect(ctx, qx - 22, qy - 22, qr + 44, qr + 44, 16);
    ctx.stroke();
    ctx.strokeStyle = wine;
    ctx.lineWidth = 3;
    roundRect(ctx, qx - 14, qy - 14, qr + 28, qr + 28, 12);
    ctx.stroke();
    const wn = await loadQrImage(buildQrSrc(entry.data, theme, 460));
    ctx.drawImage(wn.img, qx, qy, qr, qr);
    URL.revokeObjectURL(wn.objUrl);

    // Elegant table/room number.
    ctx.fillStyle = wine;
    ctx.font = `700 58px ${serif}`;
    ctx.fillText(entry.label, W / 2, qy + qr + 100);
    if (entry.subLabel) {
      ctx.fillStyle = gold;
      ctx.font = `italic 600 26px ${serif}`;
      ctx.fillText(entry.subLabel, W / 2, qy + qr + 138);
    }

    return await new Promise((res) =>
      canvas.toBlob((b) => res(b), 'image/png'),
    );
  }

  // ── Vintage Cafe: paper texture, coffee-stain watermark, tan border,
  //    handwritten font. ─────────────────────────────────────────────────
  if (theme.cafe) {
    const coffee = theme.headerText; // #6F4E37
    const tan = theme.border; // #D4A373
    const paper = theme.panel; // #FFF8F0
    const script =
      '"Segoe Script", "Brush Script MT", "Comic Sans MS", cursive';

    // Paper background.
    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, W, H);

    // Subtle paper texture — faint coffee-toned specks.
    ctx.fillStyle = 'rgba(111,78,55,0.05)';
    for (let i = 0; i < 700; i++) {
      const sx = Math.random() * W;
      const sy = Math.random() * H;
      ctx.fillRect(sx, sy, 1.4, 1.4);
    }

    // Coffee-stain ring watermarks (very faint).
    const stain = (x, y, r) => {
      ctx.strokeStyle = 'rgba(111,78,55,0.10)';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(x, y, r + 9, 0, Math.PI * 2);
      ctx.stroke();
    };
    stain(150, 250, 78);
    stain(W - 120, H - 180, 60);

    // Tan rounded border (double).
    ctx.strokeStyle = tan;
    ctx.lineWidth = 7;
    roundRect(ctx, 20, 20, W - 40, H - 40, 30);
    ctx.stroke();
    ctx.lineWidth = 2;
    roundRect(ctx, 34, 34, W - 68, H - 68, 24);
    ctx.stroke();

    ctx.textAlign = 'center';

    // Handwritten header.
    ctx.fillStyle = coffee;
    ctx.font = `700 48px ${script}`;
    ctx.fillText(theme.tagline, W / 2, 116);
    // Small underline flourish.
    ctx.strokeStyle = tan;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 90, 134);
    ctx.lineTo(W / 2 + 90, 134);
    ctx.stroke();

    // QR with a tan rounded border on paper.
    const qr = 340;
    const qx = (W - qr) / 2;
    const qy = 188;
    ctx.strokeStyle = tan;
    ctx.lineWidth = 6;
    roundRect(ctx, qx - 20, qy - 20, qr + 40, qr + 40, 18);
    ctx.stroke();
    const caf = await loadQrImage(buildQrSrc(entry.data, theme, 460));
    ctx.drawImage(caf.img, qx, qy, qr, qr);
    URL.revokeObjectURL(caf.objUrl);

    // Handwritten table/room number.
    ctx.fillStyle = coffee;
    ctx.font = `700 64px ${script}`;
    ctx.fillText(entry.label, W / 2, qy + qr + 104);
    if (entry.subLabel) {
      ctx.fillStyle = tan;
      ctx.font = `600 30px ${script}`;
      ctx.fillText(entry.subLabel, W / 2, qy + qr + 146);
    }

    return await new Promise((res) =>
      canvas.toBlob((b) => res(b), 'image/png'),
    );
  }

  // ── Framed layout (e.g. Aqua Bistro): everything sits inside one cream card
  //    floating on the coloured background, with the table/room number printed
  //    inside the card. ───────────────────────────────────────────────────
  if (theme.framed) {
    // The cream card IS the whole tile — no coloured background around it,
    // just a thin coloured rounded border (as in the reference image).
    ctx.fillStyle = theme.panel || '#FFFFFF';
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = theme.bg[0];
    ctx.lineWidth = 10;
    roundRect(ctx, 16, 16, W - 32, H - 32, 32);
    ctx.stroke();

    ctx.textAlign = 'center';

    // Coral header.
    ctx.fillStyle = theme.headerText;
    ctx.font = '800 42px "DM Sans", Arial, sans-serif';
    ctx.fillText(theme.tagline.toUpperCase(), W / 2, 116);

    // QR with a coral rounded border.
    const qr = 360;
    const qx = (W - qr) / 2;
    const qy = 168;
    ctx.strokeStyle = theme.border;
    ctx.lineWidth = 10;
    roundRect(ctx, qx - 24, qy - 24, qr + 48, qr + 48, 22);
    ctx.stroke();
    const framed = await loadQrImage(buildQrSrc(entry.data, theme, 460));
    ctx.drawImage(framed.img, qx, qy, qr, qr);
    URL.revokeObjectURL(framed.objUrl);

    // Table / room number.
    ctx.fillStyle = theme.labelText;
    ctx.font = '800 64px "DM Sans", Arial, sans-serif';
    ctx.fillText(entry.label, W / 2, qy + qr + 104);
    if (entry.subLabel) {
      ctx.font = '700 26px Arial, sans-serif';
      ctx.fillText(entry.subLabel, W / 2, qy + qr + 146);
    }

    return await new Promise((res) =>
      canvas.toBlob((b) => res(b), 'image/png'),
    );
  }

  // Decorative frame for rich themes.
  if (theme.rich) {
    ctx.strokeStyle = theme.border;
    ctx.lineWidth = 4;
    roundRect(ctx, 24, 24, W - 48, H - 48, 28);
    ctx.stroke();
  }

  ctx.textAlign = 'center';

  // Company name (truncate if very long so it never overflows the card).
  const name =
    companyName.length > 26 ? `${companyName.slice(0, 25)}…` : companyName;
  ctx.fillStyle = theme.headerText;
  ctx.font = '700 36px "DM Sans", Arial, sans-serif';
  ctx.fillText(name, W / 2, 96);

  // Tagline.
  ctx.font = '600 18px Arial, sans-serif';
  ctx.fillText(theme.tagline.toUpperCase(), W / 2, 132);

  // White panel that holds the QR.
  const panel = 420;
  const px = (W - panel) / 2;
  const py = 168;
  ctx.fillStyle = theme.panel || '#FFFFFF';
  roundRect(ctx, px, py, panel, panel, 24);
  ctx.fill();

  // QR image — request at high resolution for a crisp print.
  const { img, objUrl } = await loadQrImage(buildQrSrc(entry.data, theme, 460));
  const qr = 360;
  ctx.drawImage(img, (W - qr) / 2, py + (panel - qr) / 2, qr, qr);
  URL.revokeObjectURL(objUrl);

  // Big table/room label.
  ctx.fillStyle = theme.labelText;
  ctx.font = '800 58px "DM Sans", Arial, sans-serif';
  ctx.fillText(entry.label, W / 2, py + panel + 92);

  // Sub label (area / room name).
  if (entry.subLabel) {
    ctx.font = '500 22px Arial, sans-serif';
    ctx.fillText(entry.subLabel, W / 2, py + panel + 128);
  }

  return await new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'));
};

const safeFile = (s) => String(s || '').replace(/[^\w.-]+/g, '_');

/* ── On-screen preview card ───────────────────────────────────────────────── */
const QrCard = ({ entry, theme, onDownload, downloading }) => (
  <Card
    sx={{
      position: 'relative',
      borderRadius: 4,
      overflow: 'hidden',
      border: theme.rajasthan
        ? `3px double ${theme.border}`
        : theme.framed
          ? `3px solid ${theme.bg[0]}`
          : theme.cafe || theme.wine
            ? `3px solid ${theme.border}`
            : theme.rich
              ? `2px solid ${theme.border}`
              : `1px solid ${theme.border}`,
      // Cream-card themes (framed / rajasthan / cafe / wine) fill the tile; for
      // cafe we layer a faint paper texture over the paper background.
      background: theme.cafe
        ? `radial-gradient(circle at 20% 30%, rgba(111,78,55,0.04) 0 2px, transparent 3px),
             radial-gradient(circle at 70% 60%, rgba(111,78,55,0.04) 0 2px, transparent 3px),
             ${theme.panel}`
        : theme.framed || theme.rajasthan || theme.wine
          ? theme.panel
          : `linear-gradient(135deg, ${theme.bg[0]} 0%, ${theme.bg[1]} 100%)`,
      boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      p: 2,
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 14px 32px rgba(15,23,42,0.18)',
      },
    }}>
    {/* Rajasthani gold corner rosettes */}
    {theme.rajasthan &&
      [
        { top: 8, left: 8 },
        { top: 8, right: 8 },
        { bottom: 8, left: 8 },
        { bottom: 8, right: 8 },
      ].map((pos, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 9,
            height: 9,
            borderRadius: '50%',
            bgcolor: theme.border,
            boxShadow: `0 0 0 3px ${theme.panel}, 0 0 0 4px ${theme.border}`,
            ...pos,
          }}
        />
      ))}
    {/* Wine Cellar — faint wine-glass watermark */}
    {theme.wine && (
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 12,
          opacity: 0.12,
          pointerEvents: 'none',
        }}>
        <svg
          width='44'
          height='58'
          viewBox='0 0 44 58'
          fill='none'
          stroke='#722F37'
          strokeWidth='2'>
          <path d='M9 4 H35 C35 19 29 26 22 26 C15 26 9 19 9 4 Z' />
          <line x1='22' y1='26' x2='22' y2='48' />
          <line x1='12' y1='52' x2='32' y2='52' />
        </svg>
      </Box>
    )}
    {/* Vintage Cafe — faint coffee-stain ring watermark */}
    {theme.cafe && (
      <Box
        sx={{
          position: 'absolute',
          top: 18,
          right: 14,
          width: 64,
          height: 64,
          borderRadius: '50%',
          border: `7px solid rgba(111,78,55,0.10)`,
          boxShadow: '0 0 0 3px rgba(111,78,55,0.06)',
          pointerEvents: 'none',
        }}
      />
    )}
    {theme.wine ? (
      /* Wine Cellar Premium — elegant serif header with gold underline,
         gold/burgundy-bordered QR, burgundy number. */
      <>
        <Typography
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 700,
            fontSize: 15,
            letterSpacing: '0.06em',
            color: theme.headerText,
            mt: 0.5,
            mb: 0.3,
          }}>
          {theme.tagline.toUpperCase()}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            mb: 1.2,
          }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              bgcolor: theme.accent,
              transform: 'rotate(45deg)',
            }}
          />
          <Box sx={{ width: 70, height: '2px', bgcolor: theme.accent }} />
          <Box
            sx={{
              width: 7,
              height: 7,
              bgcolor: theme.accent,
              transform: 'rotate(45deg)',
            }}
          />
        </Box>
        <Box
          sx={{
            bgcolor: theme.qrLight,
            border: `3px solid ${theme.accent}`,
            outline: `2px solid ${theme.headerText}`,
            outlineOffset: '2px',
            borderRadius: 1.5,
            p: 1,
            zIndex: 1,
          }}>
          <img
            src={buildQrSrc(entry.data, theme, 220)}
            alt={`QR ${entry.label}`}
            width={140}
            height={140}
            loading='lazy'
            style={{ display: 'block' }}
          />
        </Box>
        <Typography
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 700,
            fontSize: 26,
            color: theme.labelText,
            mt: 1.4,
            lineHeight: 1.1,
          }}>
          {entry.label}
        </Typography>
        {entry.subLabel && (
          <Typography
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontWeight: 600,
              fontSize: 12,
              color: theme.accent,
              mb: 1,
            }}>
            {entry.subLabel}
          </Typography>
        )}
      </>
    ) : theme.cafe ? (
      /* Vintage Cafe — handwritten header, tan-bordered QR, paper feel. */
      <>
        <Typography
          sx={{
            fontFamily:
              '"Segoe Script", "Brush Script MT", "Comic Sans MS", cursive',
            fontWeight: 700,
            fontSize: 24,
            color: theme.headerText,
            mt: 0.5,
            mb: 0.2,
            lineHeight: 1,
          }}>
          {theme.tagline}
        </Typography>
        <Box
          sx={{
            width: 90,
            height: '2px',
            bgcolor: theme.border,
            borderRadius: 2,
            mb: 1.2,
          }}
        />
        <Box
          sx={{
            bgcolor: theme.qrLight,
            border: `3px solid ${theme.border}`,
            borderRadius: 2,
            p: 1,
            zIndex: 1,
          }}>
          <img
            src={buildQrSrc(entry.data, theme, 220)}
            alt={`QR ${entry.label}`}
            width={140}
            height={140}
            loading='lazy'
            style={{ display: 'block' }}
          />
        </Box>
        <Typography
          sx={{
            fontFamily:
              '"Segoe Script", "Brush Script MT", "Comic Sans MS", cursive',
            fontWeight: 700,
            fontSize: 28,
            color: theme.labelText,
            mt: 1,
            lineHeight: 1.1,
          }}>
          {entry.label}
        </Typography>
        {entry.subLabel && (
          <Typography
            sx={{
              fontFamily:
                '"Segoe Script", "Brush Script MT", "Comic Sans MS", cursive',
              fontWeight: 600,
              fontSize: 14,
              color: theme.border,
              mb: 1,
            }}>
            {entry.subLabel}
          </Typography>
        )}
      </>
    ) : theme.rajasthan ? (
      /* Royal Rajasthan — arch header, gold-bordered QR, serif royal font. */
      <>
        <Box
          sx={{
            mt: 0.5,
            mb: 1.3,
            px: 2.5,
            py: 0.7,
            bgcolor: theme.headerText,
            color: theme.border,
            border: `2px solid ${theme.border}`,
            borderRadius: '38px 38px 6px 6px', // dome / arch top
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 700,
            fontSize: 12.5,
            letterSpacing: '0.08em',
          }}>
          {theme.tagline.toUpperCase()}
        </Box>
        <Box
          sx={{
            bgcolor: theme.qrLight,
            border: `3px solid ${theme.border}`,
            borderRadius: 1.5,
            p: 1,
          }}>
          <img
            src={buildQrSrc(entry.data, theme, 220)}
            alt={`QR ${entry.label}`}
            width={140}
            height={140}
            loading='lazy'
            style={{ display: 'block' }}
          />
        </Box>
        <Typography
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 700,
            fontSize: 26,
            color: theme.labelText,
            mt: 1.2,
            lineHeight: 1.1,
          }}>
          {entry.label}
        </Typography>
        {entry.subLabel && (
          <Typography
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontWeight: 600,
              fontSize: 12,
              color: theme.border,
              mb: 1,
            }}>
            {entry.subLabel}
          </Typography>
        )}
      </>
    ) : theme.framed ? (
      /* Framed layout — content sits directly on the cream card (the card
         fills the whole tile), with a coloured border and the number below. */
      <>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 15,
            letterSpacing: '0.06em',
            color: theme.headerText,
            mt: 0.5,
            mb: 1.2,
          }}>
          {theme.tagline.toUpperCase()}
        </Typography>
        <Box
          sx={{
            bgcolor: theme.qrLight,
            border: `3px solid ${theme.border}`,
            borderRadius: 2,
            p: 1,
          }}>
          <img
            src={buildQrSrc(entry.data, theme, 220)}
            alt={`QR ${entry.label}`}
            width={140}
            height={140}
            loading='lazy'
            style={{ display: 'block' }}
          />
        </Box>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 26,
            color: theme.labelText,
            mt: 1.2,
            lineHeight: 1.1,
          }}>
          {entry.label}
        </Typography>
        {entry.subLabel && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: theme.labelText,
              opacity: 0.9,
              mb: 1,
            }}>
            {entry.subLabel}
          </Typography>
        )}
      </>
    ) : (
      <>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 15,
            color: theme.headerText,
            textAlign: 'center',
            lineHeight: 1.2,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {companyName}
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: theme.headerText,
            mb: 1.2,
            opacity: 0.9,
          }}>
          {theme.tagline.toUpperCase()}
        </Typography>

        <Box
          sx={{
            bgcolor: theme.panel || '#fff',
            borderRadius: 2.5,
            p: 1.2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}>
          {/* loading=lazy keeps offscreen cards from all hitting qrserver at once */}
          <img
            src={buildQrSrc(entry.data, theme, 220)}
            alt={`QR ${entry.label}`}
            width={150}
            height={150}
            loading='lazy'
            style={{ display: 'block' }}
          />
        </Box>

        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 22,
            color: theme.labelText,
            mt: 1.2,
            lineHeight: 1.1,
            textAlign: 'center',
          }}>
          {entry.label}
        </Typography>
        {entry.subLabel && (
          <Typography
            sx={{ fontSize: 11, color: theme.labelText, opacity: 0.85, mb: 1 }}>
            {entry.subLabel}
          </Typography>
        )}
      </>
    )}

    <Button
      size='small'
      variant='contained'
      startIcon={
        downloading ? (
          <CircularProgress size={14} color='inherit' />
        ) : (
          <Download sx={{ fontSize: 16 }} />
        )
      }
      disabled={downloading}
      onClick={() => onDownload(entry)}
      sx={{
        mt: 0.5,
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 2,
        // Cream-card themes (framed / rajasthan / cafe / wine) → solid button
        // in the theme colour; other rich themes → translucent pill on their
        // gradient; simple → dark button.
        bgcolor:
          theme.framed || theme.rajasthan || theme.cafe || theme.wine
            ? theme.headerText
            : theme.rich
              ? 'rgba(255,255,255,0.18)'
              : '#111827',
        backdropFilter:
          theme.rich &&
          !theme.framed &&
          !theme.rajasthan &&
          !theme.cafe &&
          !theme.wine
            ? 'blur(4px)'
            : 'none',
        color:
          theme.rich &&
          !theme.framed &&
          !theme.rajasthan &&
          !theme.cafe &&
          !theme.wine
            ? theme.labelText
            : '#fff',
        border:
          theme.rich &&
          !theme.framed &&
          !theme.rajasthan &&
          !theme.cafe &&
          !theme.wine
            ? `1px solid ${theme.border}`
            : 'none',
        '&:hover': {
          bgcolor: theme.framed
            ? '#E04E28'
            : theme.rajasthan
              ? '#5E0018'
              : theme.cafe
                ? '#5A3F2C'
                : theme.wine
                  ? '#5A252C'
                  : theme.rich
                    ? 'rgba(255,255,255,0.3)'
                    : '#000',
        },
      }}>
      {downloading ? 'Saving…' : 'Download'}
    </Button>
  </Card>
);

/* ── Main component ───────────────────────────────────────────────────────── */
export default function QrCodeGenerator() {
  const [mode, setMode] = useState('tables'); // 'tables' | 'rooms'
  const [themeKey, setThemeKey] = useState('simple');
  const [tables, setTables] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [bulk, setBulk] = useState(null); // { done, total } while downloading all
  const [activeArea, setActiveArea] = useState(0); // selected area tab (tables)

  const theme = useMemo(
    () => THEMES.find((t) => t.key === themeKey) || THEMES[0],
    [themeKey],
  );

  // ── Fetch all Tables (across every area) + all Rooms ──────────────────────
  const loadData = useCallback(async () => {
    const cid = getCompanyId();
    if (!cid) {
      setError('Company not found. Please log in again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      // Tables: areas first, then tables per area (same APIs as CreateOrder).
      const areasRes = await fetch(
        `${config.API_BASE_URL}/Home/GetAreas/${cid}`,
        {
          headers,
        },
      );
      const areas = areasRes.ok ? (await areasRes.json()) || [] : [];
      const perArea = await Promise.all(
        areas.map(async (a) => {
          try {
            const r = await fetch(
              `${config.API_BASE_URL}/Home/GetTables/${a.id}/${cid}`,
              { headers },
            );
            const list = r.ok ? (await r.json()) || [] : [];
            return list.map((t) => ({ ...t, areaName: a.areaName }));
          } catch {
            return [];
          }
        }),
      );
      setTables(perArea.flat());

      // Rooms: all rooms (same API as RoomList).
      const roomsRes = await fetch(
        `${config.API_BASE_URL}/home/GetAllRoom/${encodeURIComponent(cid)}`,
        { headers },
      );
      setRooms(roomsRes.ok ? (await roomsRes.json()) || [] : []);
    } catch (e) {
      console.error('[QrGenerator] data load failed:', e);
      setError('Failed to load tables / rooms. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Normalise tables/rooms into a single { id, label, subLabel, data, fileName }
  // shape so the card + download code is identical for both modes.
  const entries = useMemo(() => {
    if (mode === 'tables') {
      return tables.map((t) => {
        const tableName = t.tableName ?? t.TableName ?? '';
        return {
          id: `t-${t.tableId ?? tableName}`,
          label: tableName,
          subLabel: t.areaName || 'Table',
          data: buildOrderUrl({ tablename: tableName }),
          fileName: `Table-${safeFile(tableName)}.png`,
        };
      });
    }
    return rooms.map((r) => {
      const roomNo = r.roomnumber ?? r.roomNo ?? r.RoomNo ?? '';
      return {
        id: `r-${roomNo}`,
        label: String(roomNo),
        subLabel: r.roomName || r.type || 'Room',
        // Rooms use the SAME `tablename` param as tables — the backend treats
        // it as the table/room identifier (e.g. ?client=Hotelgauravi&tablename=101).
        data: buildOrderUrl({ tablename: roomNo }),
        fileName: `Room-${safeFile(roomNo)}.png`,
      };
    });
  }, [mode, tables, rooms]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        (e.subLabel || '').toLowerCase().includes(q),
    );
  }, [entries, search]);

  // Single download.
  const handleDownload = useCallback(
    async (entry) => {
      setDownloadingId(entry.id);
      try {
        const blob = await renderCardToBlob(entry, theme);
        if (blob) saveAs(blob, entry.fileName);
        else throw new Error('Could not create image');
      } catch (e) {
        console.error('[QrGenerator] download failed:', e);
        toast.error(`Could not download QR for ${entry.label}`);
      } finally {
        setDownloadingId(null);
      }
    },
    [theme],
  );

  // Bulk download — sequential (one QR at a time) with a small delay between
  // each, so neither the qrserver service nor the browser is overloaded even
  // for hundreds of tables/rooms.
  // Generic bulk download — used by "Download All" AND each area's "Download"
  // button. Sequential with a small delay so neither qrserver nor the browser
  // is overloaded even for hundreds of QRs.
  const handleDownloadMany = useCallback(
    async (list) => {
      if (!list?.length) return;
      setBulk({ done: 0, total: list.length });
      let failures = 0;
      for (let i = 0; i < list.length; i++) {
        try {
          const blob = await renderCardToBlob(list[i], theme);
          if (blob) saveAs(blob, list[i].fileName);
          else failures++;
        } catch (e) {
          failures++;
          console.error('[QrGenerator] bulk item failed:', list[i].label, e);
        }
        setBulk({ done: i + 1, total: list.length });
        await sleep(200); // gentle throttle — keeps load light
      }
      setBulk(null);
      if (failures)
        toast.warn(`Done — ${failures} QR(s) could not be generated`);
      else toast.success(`Downloaded ${list.length} QR code(s)`);
    },
    [theme],
  );
  const handleDownloadAll = useCallback(
    () => handleDownloadMany(filtered),
    [handleDownloadMany, filtered],
  );

  // For Tables mode: group the QR cards by their area so each area gets its own
  // section + a "Download" button for just that area. Rooms have no areas → null.
  const groupedByArea = useMemo(() => {
    if (mode !== 'tables') return null;
    const map = new Map();
    filtered.forEach((e) => {
      const area = e.subLabel || 'Other';
      if (!map.has(area)) map.set(area, []);
      map.get(area).push(e);
    });
    return Array.from(map.entries()); // [ [areaName, entries[]], ... ]
  }, [filtered, mode]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <QrCode2 sx={{ color: '#1A56DB', fontSize: 30 }} />
        <Typography
          variant='h4'
          sx={{
            fontWeight: 800,
            color: '#0F172A',
            fontSize: { xs: '1.35rem', md: '1.8rem' },
          }}>
          QR Code Generator
        </Typography>
      </Box>
      <Typography sx={{ color: '#64748B', fontSize: 14, mb: 2.5 }}>
        Generate scannable QR codes for every table and room. Pick a theme,
        download one, or download all in one click.
      </Typography>

      {/* QR digital-menu: per-table access mode + enable/disable. */}
      <QrAccessSettings />

      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
        }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size='small'
          onChange={(_, v) => v && setMode(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}>
          <ToggleButton value='tables'>
            <TableRestaurant sx={{ fontSize: 18, mr: 0.8 }} /> Tables
          </ToggleButton>
          <ToggleButton value='rooms'>
            <KingBed sx={{ fontSize: 18, mr: 0.8 }} /> Rooms
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size='small'
          placeholder={`Search ${mode}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: '0 1 240px' }}
        />

        <Box sx={{ flex: 1 }} />

        <Tooltip title='Reload tables & rooms'>
          <span>
            <Button
              size='small'
              variant='outlined'
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading || !!bulk}
              sx={{ textTransform: 'none', borderRadius: 2 }}>
              Refresh
            </Button>
          </span>
        </Tooltip>

        <Button
          variant='contained'
          startIcon={
            bulk ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <CloudDownload />
            )
          }
          onClick={handleDownloadAll}
          disabled={loading || !!bulk || filtered.length === 0}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: '#1A56DB',
            '&:hover': { bgcolor: '#1648b8' },
          }}>
          {bulk
            ? `Downloading ${bulk.done}/${bulk.total}`
            : `Download All (${filtered.length})`}
        </Button>
      </Box>

      {/* Theme picker */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 2.5,
          p: 1.5,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          background: '#F8FAFC',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mr: 1 }}>
          <Palette sx={{ fontSize: 18, color: '#64748B' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
            THEME
          </Typography>
        </Box>
        {THEMES.map((t) => {
          const active = t.key === themeKey;
          return (
            <Box
              key={t.key}
              onClick={() => setThemeKey(t.key)}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.4,
                py: 0.8,
                borderRadius: 2,
                border: active ? '2px solid #1A56DB' : '1px solid #E2E8F0',
                background: '#fff',
                boxShadow: active ? '0 2px 8px rgba(26,86,219,0.18)' : 'none',
                transition: 'all 0.15s',
              }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${t.bg[0]}, ${t.bg[1]})`,
                  border: `1px solid ${t.border}`,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? '#1A56DB' : '#475569',
                }}>
                {t.name}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {bulk && (
        <LinearProgress
          variant='determinate'
          value={(bulk.done / bulk.total) * 100}
          sx={{ mb: 2, borderRadius: 1, height: 6 }}
        />
      )}

      {/* Body */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, color: '#64748B' }}>
            Loading {mode}…
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity='error' sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert severity='info' sx={{ borderRadius: 2 }}>
          {search
            ? `No ${mode} match “${search}”.`
            : `No ${mode} found for this property.`}
        </Alert>
      ) : groupedByArea ? (
        /* Tables → AREA TABS: click a tab to see only that area's QRs */
        (() => {
          const idx = Math.min(activeArea, groupedByArea.length - 1);
          const [areaName, areaEntries] = groupedByArea[idx] || ['', []];
          return (
            <>
              <Tabs
                value={idx}
                onChange={(_, v) => setActiveArea(v)}
                variant='scrollable'
                scrollButtons='auto'
                allowScrollButtonsMobile
                sx={{
                  mb: 2,
                  borderBottom: '1px solid #E2E8F0',
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 },
                }}>
                {groupedByArea.map(([name, list]) => (
                  <Tab key={name} label={`${name} (${list.length})`} />
                ))}
              </Tabs>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1.5,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableRestaurant sx={{ color: '#1A56DB', fontSize: 22 }} />
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.05rem',
                      color: '#0F172A',
                    }}>
                    {areaName}
                  </Typography>
                  <Chip
                    label={`${areaEntries.length} table${areaEntries.length > 1 ? 's' : ''}`}
                    size='small'
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<Download />}
                  disabled={loading || !!bulk}
                  onClick={() => handleDownloadMany(areaEntries)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    fontWeight: 700,
                  }}>
                  Download ({areaEntries.length})
                </Button>
              </Box>
              <Grid container spacing={2}>
                {areaEntries.map((entry) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={entry.id}>
                    <QrCard
                      entry={entry}
                      theme={theme}
                      onDownload={handleDownload}
                      downloading={downloadingId === entry.id}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          );
        })()
      ) : (
        <>
          <Chip
            label={`${filtered.length} ${mode}`}
            size='small'
            sx={{ mb: 1.5, fontWeight: 600 }}
          />
          <Grid container spacing={2}>
            {filtered.map((entry) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={entry.id}>
                <QrCard
                  entry={entry}
                  theme={theme}
                  onDownload={handleDownload}
                  downloading={downloadingId === entry.id}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}
